export type LogLevel = "debug" | "info" | "warn" | "error";
export type LogContext = Record<string, string | number | boolean | undefined>;

export interface Logger {
  debug(msg: string, ctx?: LogContext): void;
  info(msg: string, ctx?: LogContext): void;
  warn(msg: string, ctx?: LogContext): void;
  error(msg: string, ctx?: LogContext): void;
  child(ctx: LogContext): Logger;
}

const LEVEL_RANK: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

// Browser detection: Workers and Node lack `document`; browsers have it.
const isBrowser = typeof document !== "undefined";

function emit(level: LogLevel, name: string, msg: string, ctx: LogContext): void {
  const fn = level === "error" ? console.error : level === "warn" ? console.warn : console.log;
  if (isBrowser) {
    // Pretty output for browser DevTools: extra context as a second expandable arg.
    const hasCtx = Object.values(ctx).some((v) => v !== undefined);
    fn(`[${name}] ${level}: ${msg}`, ...(hasCtx ? [ctx] : []));
  } else {
    // Structured JSON for Cloudflare observability and Node; undefined values are omitted by JSON.stringify.
    fn(JSON.stringify({ level, name, msg, t: Date.now(), ...ctx }));
  }
}

export function createLogger(name: string, baseCtx: LogContext = {}, minLevel: LogLevel = "info"): Logger {
  const minRank = LEVEL_RANK[minLevel];

  function log(level: LogLevel, msg: string, ctx: LogContext = {}): void {
    if (LEVEL_RANK[level] < minRank) return;
    emit(level, name, msg, { ...baseCtx, ...ctx });
  }

  return {
    debug: (msg, ctx) => log("debug", msg, ctx),
    info:  (msg, ctx) => log("info", msg, ctx),
    warn:  (msg, ctx) => log("warn", msg, ctx),
    error: (msg, ctx) => log("error", msg, ctx),
    child: (ctx) => createLogger(name, { ...baseCtx, ...ctx }, minLevel),
  };
}
